import { Controller, FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';

import { AddAutomationButton } from '../../AutomationList/components/AddAutomationButton';
import { ATSAutomationCreateTestIds } from 'data-testids/ATS';
import {
  AutomationModalActionType,
  AutomationModalPaneOptions,
  AutomationTrigger,
} from 'features/Automations/enums';
import { CreateAutomationCTAButtons } from './CreateAutomationCTAButtons';
import { Automation, CreateFormProps } from 'features/Automations/types';
import { ErrorText } from 'features/Automations/styles';
import { IndividualAutomation } from './IndividualAutomation';
import { QuestionComboBox } from './QuestionComboBox';
import { useAutomation } from 'features/Automations/context/AutomationContext';
import { Question } from 'types/role';

export const CreateForm: React.FC<CreateFormProps> = ({ automationState, automationDispatch }) => {
  const { roleId, questions, onChangeModalPane, selectedQuestionId, setSelectedQuestionId } =
    useAutomation();
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [loadingStates, setLoadingStates] = useState<Record<number, boolean>>({});
  const [validationStates, setValidationStates] = useState<Record<number, boolean>>({});

  const handleLoadingChange = useCallback((index: number, isLoading: boolean) => {
    setLoadingStates((prev) => ({
      ...prev,
      [index]: isLoading,
    }));
  }, []);

  const handleValidationChange = useCallback((index: number, hasError: boolean) => {
    setValidationStates((prev) => ({
      ...prev,
      [index]: hasError,
    }));
  }, []);

  const checkForDuplicateAnswers = useCallback((automations: Automation[]): boolean => {
    const answerCounts = new Map();
    automations?.forEach((automation: Automation) => {
      const answer = automation?.trigger?.answer;
      if (answer) {
        answerCounts.set(answer, (answerCounts.get(answer) || 0) + 1);
      }
    });
    return Array.from(answerCounts.values()).some((count) => count > 1);
  }, []);

  const methods = useForm({
    defaultValues: {
      automations: [],
    },
    mode: 'onChange',
    criteriaMode: 'firstError',
    reValidateMode: 'onChange',
  });
  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = methods;
  const { fields, append, remove } = useFieldArray({ control, name: 'automations' });
  const hasSelectedQuestion = Boolean(selectedQuestion);
  const isAnyLoading = Object.values(loadingStates).some((isLoading) => isLoading);
  const isAnyInvalid = Object.values(validationStates).some((hasError) => hasError);

  // using useWatch to watch the automations field and validate for duplicates
  // watch was not triggering when dropdown value changed so we are using useWatch as it
  // re-renders when the dropdown value changes
  const watchedAutomations = useWatch({
    control,
    name: 'automations',
  });

  useEffect(() => {
    if (watchedAutomations) {
      const hasDuplicates = checkForDuplicateAnswers(watchedAutomations);

      if (hasDuplicates) {
        setError('automations', {
          type: 'duplicate',
          message: 'Multiple automations cannot be created for the same answer',
        });
      } else {
        clearErrors('automations');
      }
    }
  }, [watchedAutomations, setError, clearErrors, checkForDuplicateAnswers]);

  const handleQuestionSelection = useCallback(
    (data: Question | null) => {
      if (!data || !data.id) {
        setSelectedQuestion(null);
        reset({ automations: [] }, { keepDirty: false });
        return;
      }

      setSelectedQuestion(data);

      const existingAutomationsForQuestion =
        automationState.automations?.filter(
          (automation) => automation.trigger.question_id === data.id
        ) || [];

      let newFormState;

      if (existingAutomationsForQuestion.length === 0) {
        const defaultAutomation = {
          role_id: roleId,
          order: 0,
          trigger: {
            trigger_type: AutomationTrigger.QUESTION_ANSWER,
            question_id: data.id,
            question_type: data.question_type,
            answer: 'yes',
          },
          action: {
            action_type: AutomationModalActionType.UPDATE_STATUS,
          },
          email: {
            email_template_id: null,
            sender: null,
          },
        };

        newFormState = {
          automations: [defaultAutomation],
        };

        append(defaultAutomation);
      } else {
        newFormState = {
          automations: existingAutomationsForQuestion,
        };

        reset(newFormState, { keepDirty: false });
      }
    },
    [automationState.automations, reset, roleId, append]
  );

  useEffect(() => {
    if (selectedQuestionId && questions.length > 0) {
      const questionToSelect = questions.find((q) => q.id === selectedQuestionId);
      if (questionToSelect) {
        handleQuestionSelection(questionToSelect as Question);
        setSelectedQuestionId(null);
      }
    }
  }, [selectedQuestionId, questions, setSelectedQuestionId, handleQuestionSelection]);

  const onSave = (data) => {
    if (!selectedQuestion) {
      return;
    }

    const existingAutomations = automationState.automations || [];
    const existingAutomationsForQuestion = existingAutomations.filter(
      (automation) => automation.trigger.question_id === selectedQuestion.id
    );
    const otherAutomations = existingAutomations.filter(
      (automation) => automation.trigger.question_id !== selectedQuestion.id
    );

    // When saving automations for a question, we need to check if there are already automations for that question
    // If there are, preserve their order values
    const updatedAutomationsForQuestion = preserveAutomationOrderValues(
      data.automations,
      existingAutomationsForQuestion
    );
    const updatedAutomations = [...otherAutomations, ...updatedAutomationsForQuestion];

    automationDispatch({
      type: 'SET_AUTOMATION_LIST',
      payload: {
        automations: updatedAutomations,
        hasUnsavedChanges: true,
      },
    });
    onChangeModalPane(AutomationModalPaneOptions.LIST);
  };

  const handleAddAutomationClick = () => {
    if (!selectedQuestion) {
      return;
    }

    append({
      role_id: roleId,
      order: fields.length,
      trigger: {
        trigger_type: AutomationTrigger.QUESTION_ANSWER,
        question_id: selectedQuestion.id,
        question_type: selectedQuestion.question_type,
        answer: 'yes',
      },
      action: {
        action_type: AutomationModalActionType.UPDATE_STATUS,
      },
    });

    onChangeModalPane(AutomationModalPaneOptions.CREATE);
  };

  const handleDeleteAutomation = (index: number) => {
    remove(index);
  };

  const preserveAutomationOrderValues = (newAutomations, existingAutomationsForQuestion) => {
    const existingOrderMap = new Map();
    existingAutomationsForQuestion.forEach((automation) => {
      if (automation.id) {
        existingOrderMap.set(automation.id, automation.order);
      }
    });

    return newAutomations.map((automation) => {
      if (automation.id && existingOrderMap.has(automation.id)) {
        return {
          ...automation,
          order: existingOrderMap.get(automation.id),
        };
      }
      const existingOrder =
        existingAutomationsForQuestion.length > 0 ? existingAutomationsForQuestion[0].order : 0;
      return {
        ...automation,
        order: existingOrder,
      };
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSave)}>
        <Controller
          name={'automations'}
          control={control}
          render={({ fieldState: { error } }) => {
            return (
              <>
                <QuestionComboBox
                  onChange={(_event, data) => {
                    return handleQuestionSelection(data);
                  }}
                  options={questions}
                  value={selectedQuestion}
                />
                {error && (
                  <ErrorText data-testid={ATSAutomationCreateTestIds.MISSING_QUESTION_ERROR_TEXT}>
                    {error.message}
                  </ErrorText>
                )}
              </>
            );
          }}
        />
        {hasSelectedQuestion && (
          <>
            {fields.map((item, index) => {
              return (
                <li key={item.id} style={{ listStyle: 'none', position: 'relative' }}>
                  <IndividualAutomation
                    fieldName={`automations.${index}.trigger.answer`}
                    index={index}
                    onDelete={handleDeleteAutomation}
                    onLoadingChange={handleLoadingChange}
                    onValidationChange={handleValidationChange}
                  />
                </li>
              );
            })}
            <Box sx={{ marginTop: 2 }}>
              <AddAutomationButton onClick={handleAddAutomationClick} />
            </Box>
          </>
        )}

        <CreateAutomationCTAButtons
          hasError={Object.keys(errors).length > 0}
          isDisabled={
            !hasSelectedQuestion ||
            Object.keys(errors).length > 0 ||
            !isDirty ||
            isAnyLoading ||
            isAnyInvalid
          }
        />
      </form>
    </FormProvider>
  );
};
